import { parse, TYPE, type MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { printAST } from '@formatjs/icu-messageformat-parser/printer.js';
import { mapCatalog, type Catalog } from './catalogs.ts';

const accents: Record<string, string> = {
    a: 'à',
    b: 'ƀ',
    c: 'ç',
    d: 'ð',
    e: 'é',
    f: 'ƒ',
    g: 'ĝ',
    h: 'ĥ',
    i: 'î',
    j: 'ĵ',
    k: 'ķ',
    l: 'ľ',
    m: 'ɱ',
    n: 'ñ',
    o: 'ô',
    p: 'þ',
    q: 'ǫ',
    r: 'ŕ',
    s: 'š',
    t: 'ţ',
    u: 'û',
    v: 'ṽ',
    w: 'ŵ',
    x: 'ẋ',
    y: 'ý',
    z: 'ž',
    A: 'Å',
    B: 'Ɓ',
    C: 'Ç',
    D: 'Ð',
    E: 'É',
    F: 'Ƒ',
    G: 'Ĝ',
    H: 'Ĥ',
    I: 'Î',
    J: 'Ĵ',
    K: 'Ķ',
    L: 'Ľ',
    M: 'Ṁ',
    N: 'Ñ',
    O: 'Ö',
    P: 'Þ',
    Q: 'Ǫ',
    R: 'Ŕ',
    S: 'Š',
    T: 'Ţ',
    U: 'Û',
    V: 'Ṽ',
    W: 'Ŵ',
    X: 'Ẋ',
    Y: 'Ý',
    Z: 'Ž',
};

function accentuate(text: string): string {
    return text.replace(/[a-zA-Z]/g, (letter) => accents[letter] ?? letter);
}

/** Only literal text is transformed: argument names, plural keywords, `#` and tags stay intact. */
function transform(elements: MessageFormatElement[]): MessageFormatElement[] {
    return elements.map((element) => {
        switch (element.type) {
            case TYPE.literal:
                return { ...element, value: accentuate(element.value) };
            case TYPE.plural:
            case TYPE.select:
                return {
                    ...element,
                    options: Object.fromEntries(
                        Object.entries(element.options).map(([rule, option]) => [
                            rule,
                            { ...option, value: transform(option.value) },
                        ]),
                    ),
                };
            case TYPE.tag:
                return { ...element, children: transform(element.children) };
            default:
                return element;
        }
    });
}

/**
 * `Remise des offres` → `⟦Réɱîšé ðéš ôƒƒŕéš ~~~~~⟧`: untranslated texts stand out (no brackets), texts grow by
 * about 30% to reveal overflows, and the brackets show truncation.
 */
export function pseudoLocalize(message: string): string {
    const ast = transform(parse(message, { captureLocation: false }));
    const visibleLength = message.replace(/\{[^}]*\}/g, '').length;
    const padding = '~'.repeat(Math.max(1, Math.round(visibleLength * 0.3)));
    return `⟦${printAST(ast)} ${padding}⟧`;
}

export function pseudoLocalizeCatalog(catalog: Catalog): Catalog {
    return mapCatalog(catalog, pseudoLocalize);
}
